import React, { useEffect, useState } from 'react';
import authService from '../../services/authService';

const AdminBilling = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amountCents, setAmountCents] = useState(1000);
  const [adjustCents, setAdjustCents] = useState(0);
  const [adjustNote, setAdjustNote] = useState('');
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [ledger, setLedger] = useState({ total: 0, items: [], page: 1, pageSize: 50 });
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadUsers = async () => {
    const t = authService.getToken();
    const res = await fetch(`/api/admin/users?page=${page}&pageSize=${pageSize}`, { headers: { Authorization: `Bearer ${t}` } });
    if (res.ok) {
      const j = await res.json();
      setUsers(j.users || []);
      // If we navigated past the last page, pull back
      const total = Number(j.total || 0);
      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      if (page > maxPage) setPage(maxPage);
    }
  };

  const loadBalance = async (userId) => {
    const t = authService.getToken();
    const res = await fetch(`/api/admin/billing/balance?userId=${encodeURIComponent(userId)}`, { headers: { Authorization: `Bearer ${t}` } });
    const ct = res.headers.get('content-type') || '';
    const raw = await res.text();
    if (!res.ok) { setError(raw || `HTTP ${res.status}`); return; }
    try { setBalanceInfo(ct.includes('application/json') && raw ? JSON.parse(raw) : null); } catch (e) { setError('Failed to parse balance.'); }
  };

  const loadLedger = async (userId, p=1, ps=50) => {
    const t = authService.getToken();
    const res = await fetch(`/api/admin/billing/ledger?userId=${encodeURIComponent(userId)}&page=${p}&pageSize=${ps}`, { headers: { Authorization: `Bearer ${t}` } });
    const ct = res.headers.get('content-type') || '';
    const raw = await res.text();
    if (!res.ok) { setError(raw || `HTTP ${res.status}`); return; }
    try { setLedger(ct.includes('application/json') && raw ? JSON.parse(raw) : { total:0, items:[], page:p, pageSize:ps }); } catch (e) { setError('Failed to parse ledger.'); }
  };

  const exportLedgerCsv = async (days) => {
    try {
      if (!selectedUser) { setError('Select a user'); return; }
      const params = new URLSearchParams();
      params.set('userId', selectedUser.id);
      if (typeof days === 'number' && days > 0) {
        const now = new Date();
        const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        params.set('start', start.toISOString());
        params.set('end', now.toISOString());
      }
      const t = authService.getToken && authService.getToken();
      const res = await fetch('/api/admin/billing/ledger/export?' + params.toString(), { credentials: 'include', headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok) { const raw = await res.text(); throw new Error(raw || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      a.download = (m && m[1]) ? m[1].replace(/"/g,'').trim() : `ledger-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message || 'Export failed'); }
  };

  const exportIncomeCsv = async (days, allUsers = false) => {
    try {
      const params = new URLSearchParams();
      if (!allUsers) {
        if (!selectedUser) { setError('Select a user'); return; }
        params.set('userId', selectedUser.id);
      }
      if (typeof days === 'number' && days > 0) {
        const now = new Date();
        const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        params.set('start', start.toISOString());
        params.set('end', now.toISOString());
      }
      const t = authService.getToken && authService.getToken();
      const res = await fetch('/api/admin/billing/income/export?' + params.toString(), { credentials: 'include', headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok) { const raw = await res.text(); throw new Error(raw || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      a.download = (m && m[1]) ? m[1].replace(/"/g,'').trim() : `income-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message || 'Export failed'); }
  };

  const exportAllUsersLedger = async (days=30) => {
    try {
      const params = new URLSearchParams();
      if (typeof days === 'number' && days > 0) {
        const now = new Date();
        const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        params.set('start', start.toISOString());
        params.set('end', now.toISOString());
      }
      const t = authService.getToken && authService.getToken();
      const res = await fetch('/api/admin/billing/ledger/export-all?' + params.toString(), { credentials:'include', headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok) { const raw = await res.text(); throw new Error(raw || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `ledger-all-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message || 'Export failed'); }
  };

  useEffect(() => { (async () => { try { setLoading(true); await loadUsers(); const t = authService.getToken(); const r = await fetch('/api/billing/config', { credentials:'include', headers: t ? { Authorization: `Bearer ${t}` } : {} }); const ct = r.headers.get('content-type')||''; const raw = await r.text(); if (r.ok) { try { setConfig(ct.includes('application/json')&&raw? JSON.parse(raw): null); } catch {} } } catch (e) { setError(e.message||'Failed'); } finally { setLoading(false);} })(); }, [page, pageSize]);
  useEffect(() => { if (selectedUser?.id) { loadBalance(selectedUser.id); loadLedger(selectedUser.id); } }, [selectedUser]);

  const grant = async () => {
    if (!selectedUser) { setError('Select a user'); return; }
    try {
      setError(''); setLoading(true);
      const t = authService.getToken();
      const res = await fetch('/api/admin/billing/grant', { method:'POST', credentials:'include', headers: {'Content-Type':'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {})}, body: JSON.stringify({ userId: selectedUser.id, amountCents: Number(amountCents||0) })});
      const raw = await res.text();
      if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);
      await loadBalance(selectedUser.id);
      setError('');
      setBalanceInfo(prev => prev); // trigger re-render
    } catch(e) { setError(e.message||'Grant failed'); }
    finally { setLoading(false); }
  };

  const adjust = async () => {
    if (!selectedUser) { setError('Select a user'); return; }
    try {
      setError(''); setLoading(true);
      const t = authService.getToken();
      const amt = Number(adjustCents || 0);
      if (amt < 0) {
        const ok = window.confirm(`Confirm debit of $${((-amt)/100).toFixed(2)} for ${selectedUser.email}?`);
        if (!ok) { setLoading(false); return; }
      }
      const res = await fetch('/api/admin/billing/adjust', { method:'POST', credentials:'include', headers: {'Content-Type':'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {})}, body: JSON.stringify({ userId: selectedUser.id, amountCents: amt, note: adjustNote })});
      const raw = await res.text();
      if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);
      await loadBalance(selectedUser.id);
      await loadLedger(selectedUser.id);
      setAdjustCents(0);
      setAdjustNote('');
    } catch(e) { setError(e.message||'Adjust failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="admin-section">
      <h2 className="admin-header">Billing ($)</h2>
      {error && <div className="error-message" style={{marginBottom:8}}>{error}</div>}
      <div className="admin-controls" style={{ gap: 8, alignItems:'baseline' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Rows:</span>
          <select 
            value={pageSize} 
            onChange={e=>setPageSize(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              cursor: 'pointer',
              minWidth: '80px'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
        </label>
        <div className="admin-pager" style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button 
            disabled={page<=1} 
            onClick={()=>setPage(p=>p-1)}
            style={{
              padding: '6px 12px',
              background: page<=1 ? '#f3f4f6' : '#fff',
              color: page<=1 ? '#9ca3af' : '#667eea',
              border: `1.5px solid ${page<=1 ? '#e5e7eb' : '#667eea'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: page<=1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: page<=1 ? 0.6 : 1
            }}
            onMouseEnter={(e)=>{
              if (page>1) {
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e)=>{
              if (page>1) {
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }
            }}
          >
            Prev
          </button>
          <span style={{ fontSize:14, color:'#374151' }}>Page {page}</span>
          <button 
            disabled={users.length < pageSize} 
            onClick={()=>setPage(p=>p+1)}
            style={{
              padding: '6px 12px',
              background: users.length < pageSize ? '#f3f4f6' : '#fff',
              color: users.length < pageSize ? '#9ca3af' : '#667eea',
              border: `1.5px solid ${users.length < pageSize ? '#e5e7eb' : '#667eea'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: users.length < pageSize ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: users.length < pageSize ? 0.6 : 1
            }}
            onMouseEnter={(e)=>{
              if (users.length >= pageSize) {
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e)=>{
              if (users.length >= pageSize) {
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }
            }}
          >
            Next
          </button>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color:'#555' }}>
          {config ? `Per-request cap: ${config.maxRequestCostCents>0 ? `$${(config.maxRequestCostCents/100).toFixed(2)}` : 'none'}` : 'Loading config…'}
        </div>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead><tr><th>Select</th><th>Email</th><th>Name</th><th>Roles</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={selectedUser?.id===u.id?'active-row':''}>
                <td><input type="radio" name="selbilluser" checked={selectedUser?.id===u.id} onChange={()=>setSelectedUser(u)} /></td>
                <td>{u.email}</td>
                <td>{(u.firstName||'') + ' ' + (u.lastName||'')}</td>
                <td>{(u.roles||[]).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <div className="admin-section" style={{ marginTop: 12 }}>
          <h3 className="admin-header" style={{ marginTop:0 }}>Balance, Grant & Adjust</h3>
          <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
            <div>Balance: <strong>{balanceInfo ? `$${((balanceInfo.balanceMicroUsd||0)/1_000_000).toFixed(6)}` : '—'}</strong></div>
            <div>Today: <strong>{balanceInfo ? `$${((balanceInfo.spendTodayMicroUsd||0)/1_000_000).toFixed(6)}` : '—'}</strong></div>
            <label style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Grant amount ($)</span>
              <input 
                type="number" 
                min="0" 
                step="0.01" 
                value={(amountCents/100).toFixed(2)} 
                onChange={e=>setAmountCents(Math.round(Number(e.target.value||0)*100))}
                style={{
                  padding: '8px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100px',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <button 
              onClick={grant} 
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: loading ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e)=>{
                if (!loading) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
              onMouseLeave={(e)=>{
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }
              }}
            >
              Grant
            </button>
            <span style={{ width:1, height:20, background:'#ddd', display:'inline-block' }} />
            <label style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Adjust ($ +/−)</span>
              <input 
                type="number" 
                step="0.01" 
                value={(adjustCents/100).toFixed(2)} 
                onChange={e=>setAdjustCents(Math.round(Number(e.target.value||0)*100))}
                style={{
                  padding: '8px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100px',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <input 
              type="text" 
              placeholder="Note (optional)" 
              value={adjustNote} 
              onChange={e=>setAdjustNote(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                background: '#fff',
                width: '180px',
                boxSizing: 'border-box'
              }}
              onFocus={(e)=>{
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e)=>{
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button 
              onClick={adjust} 
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: loading ? '#9ca3af' : '#fff',
                color: loading ? '#fff' : '#667eea',
                border: `1.5px solid ${loading ? '#9ca3af' : '#667eea'}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e)=>{
                if (!loading) {
                  e.target.style.background = '#667eea';
                  e.target.style.color = '#fff';
                }
              }}
              onMouseLeave={(e)=>{
                if (!loading) {
                  e.target.style.background = '#fff';
                  e.target.style.color = '#667eea';
                }
              }}
            >
              Apply Adjust
            </button>
            <span style={{ width:1, height:20, background:'#ddd', display:'inline-block' }} />
            <button 
              onClick={()=>exportLedgerCsv()} 
              title="Download full ledger CSV"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export Ledger CSV
            </button>
            <button 
              onClick={()=>exportLedgerCsv(30)} 
              title="Download last 30 days"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export (30d)
            </button>
            <span style={{ width:1, height:20, background:'#ddd', display:'inline-block' }} />
            <button 
              onClick={()=>exportIncomeCsv()} 
              title="Income CSV for selected user"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export Income CSV
            </button>
            <button 
              onClick={()=>exportIncomeCsv(30)} 
              title="Income (30d) for selected user"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export Income (30d)
            </button>
            <button 
              onClick={()=>exportIncomeCsv(30, true)} 
              title="Income (30d) for all users"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export Income (All Users 30d)
            </button>
            <span style={{ width:1, height:20, background:'#ddd', display:'inline-block' }} />
            <button 
              onClick={()=>exportAllUsersLedger(30)} 
              title="Ledger (30d) for all users"
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e)=>{
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e)=>{
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }}
            >
              Export Ledger (All Users 30d)
            </button>
          </div>
          <div className="table-scroll" style={{ marginTop: 12 }}>
            <table className="admin-table">
              <thead><tr><th>Date</th><th>Provider</th><th>Model</th><th>In</th><th>Out</th><th>Cost ($)</th><th>Balance ($, cents)</th><th>Reason</th></tr></thead>
              <tbody>
                {(ledger.items||[]).map(i => (
                  <tr key={i.id}>
                    <td>{new Date(i.occurredAt).toLocaleString()}</td>
                    <td>{i.provider||''}</td>
                    <td>{i.model||''}</td>
                    <td>{i.inputTokens??''}</td>
                    <td>{i.outputTokens??''}</td>
                    <td>{(i.costMicroUsd!=null) ? `$${(i.costMicroUsd/1_000_000).toFixed(6)}` : `$${((i.costCents||0)/100).toFixed(2)}`}</td>
                    <td>{i.balanceAfterCents!=null ? `$${((i.balanceAfterCents)/100).toFixed(2)}` : '—'}</td>
                    <td>{i.reason||''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBilling;


